import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import HeroIllustration from '../components/HeroIllustration'
import OrganizationCertificationLookup from '../components/OrganizationCertificationLookup'
import {
  CatalogIcon,
  TrainingIcon,
  TranscriptIcon,
  DashboardIcon,
  FeedbackIcon,
  ExportIcon,
} from '../components/FeatureIcons'
import {
  RegisterIcon,
  BrowseIcon,
  EnrollIcon,
  LearnIcon,
  CompleteIcon,
  FeedbackStepIcon,
} from '../components/LifecycleIcons'

const features = [
  {
    Icon: CatalogIcon,
    title: 'Course Catalog',
    description: 'Searchable training library filtered by role, audience, and topic.',
    highlight: 'Find the right learning path fast',
    href: '/features#comprehensive-course-catalog',
  },
  {
    Icon: TrainingIcon,
    title: 'Interactive Training',
    description: 'Lessons with video, text, and assessments — resume anytime.',
    highlight: 'Learn in a guided, flexible format',
    href: '/features#interactive-learning-experience',
  },
  {
    Icon: TranscriptIcon,
    title: 'Transcripts & Certificates',
    description: 'Printable completion records with scores and dates.',
    highlight: 'Keep verified completion in one place',
    href: '/features#official-transcripts-and-certificates',
  },
  {
    Icon: DashboardIcon,
    title: 'Progress Dashboard',
    description: 'At-a-glance view of your courses, scores, and next steps.',
    highlight: 'See momentum, scores, and readiness',
    href: '/features#personalized-dashboard',
  },
  {
    Icon: FeedbackIcon,
    title: 'Feedback & Support',
    description: 'Report issues and submit feedback without leaving the platform.',
    highlight: 'Stay connected to support and follow-up',
    href: '/features#built-in-feedback-and-support',
  },
  {
    Icon: ExportIcon,
    title: 'Data Export',
    description: 'Export metrics for external reporting and analysis tools.',
    highlight: 'Move reporting data where it is needed',
    href: '/features#data-export-and-reporting',
  },
]

const lifecycleSteps = [
  { Icon: RegisterIcon, step: 1, title: 'Register & Sign In' },
  { Icon: BrowseIcon, step: 2, title: 'Browse Catalog' },
  { Icon: EnrollIcon, step: 3, title: 'Enroll in Training' },
  { Icon: LearnIcon, step: 4, title: 'Complete Modules' },
  { Icon: FeedbackStepIcon, step: 5, title: 'Provide Feedback' },
  { Icon: CompleteIcon, step: 6, title: 'Earn Transcript' },
]

const userTypes = [
  {
    title: 'Learners',
    subtitle: 'Designated School Officials (DSOs)',
    description:
      'Create an account, browse the course catalog, complete training modules, earn certificates, and track your progress through a personalized dashboard.',
    linkText: 'For Learners',
    href: '/for-learners',
    image: '/media/images/home/learner-role.jpg',
    imagePosition: 'center 28%',
    accentLabel: 'Training in progress',
  },
  {
    title: 'Administrators',
    subtitle: 'Program Staff & Reviewers',
    description:
      'Access platform analytics, review completion metrics, audit user activity, manage feedback, and export reporting data.',
    linkText: 'For Administrators',
    href: '/for-administrators',
    image: '/media/images/home/administrator-role.jpg',
    imagePosition: 'center 42%',
    accentLabel: 'Metrics & oversight',
  },
  {
    title: 'Public',
    subtitle: 'Visitors & Prospective Learners',
    description:
      'Explore available training, learn about the platform, and create an account when you\'re ready to start your learning journey.',
    linkText: 'Features & Benefits',
    href: '/features',
    image: '/media/images/home/public-role.jpg',
    imagePosition: 'center 30%',
    accentLabel: 'Searching for answers',
  },
]

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section
        className="relative bg-gradient-to-br from-primary-darker via-primary to-secondary overflow-hidden"
        aria-labelledby="hero-heading"
      >
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1800&q=80"
            alt=""
            className="h-full w-full object-cover opacity-25 grayscale"
          />
          <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(0,43,71,0.84),rgba(0,82,136,0.78),rgba(0,120,174,0.7))]" />
        </div>

        {/* Wave overlay */}
        <div className="absolute inset-0 opacity-[0.06]" aria-hidden="true">
          <svg
            className="absolute bottom-0 w-full h-auto"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
          >
            <path
              fill="white"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,176C1248,160,1344,160,1392,160L1440,160L1440,320L0,320Z"
            />
          </svg>
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1
                id="hero-heading"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
              >
                WELCOME
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-white/90 max-w-prose leading-relaxed">
                CETA provides training for new and experienced designated
                school officials that detail the rules, regulations, and
                processes governing the F and M student life cycle in the
                United States.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-success text-white font-bold px-6 py-3 rounded text-base uppercase tracking-wider hover:bg-success-hover transition-colors"
                >
                  Get Started
                  <ArrowRight size={18} aria-hidden="true" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-6 py-3 rounded text-base uppercase tracking-wider hover:bg-white/10 transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-center" aria-hidden="true">
              <HeroIllustration />
            </div>
          </div>
        </div>
      </section>

      {/* Training Lifecycle */}
      <section
        className="py-16 sm:py-20 bg-gradient-to-br from-primary-dark to-primary overflow-hidden"
        aria-labelledby="lifecycle-heading"
      >
        <div className="mx-auto max-w-7xl px-4">
          <h2
            id="lifecycle-heading"
            className="text-2xl sm:text-3xl font-bold text-white text-center mb-4"
          >
            TRAINING LIFECYCLE
          </h2>
          <p className="text-white/70 text-center text-lg mb-12 max-w-2xl mx-auto">
            From registration to certification — your path through CETA training
          </p>

          {/* Steps */}
          <div className="relative">
            {/* Dashed connector line — desktop only */}
            <div
              className="hidden md:block absolute top-12 left-[10%] right-[10%] border-t-2 border-dashed border-white/30"
              aria-hidden="true"
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-8 md:gap-4">
              {lifecycleSteps.map(({ Icon, step, title }) => (
                <div key={step} className="flex flex-col items-center text-center relative">
                  <div className="relative z-10 mb-3">
                    <Icon />
                  </div>
                  <span className="text-secondary-light text-sm font-semibold italic">
                    Step {step}
                  </span>
                  <span className="text-white font-bold text-sm mt-1 leading-tight">
                    {title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <OrganizationCertificationLookup />

      {/* Features & Benefits */}
      <section
        className="relative overflow-hidden bg-gradient-to-b from-white via-neutral-50 to-white py-16 sm:py-20"
        aria-labelledby="features-heading"
      >
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(0,120,174,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(94,151,50,0.07),transparent_22%)]"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-4">
          <h2
            id="features-heading"
            className="text-3xl sm:text-4xl font-bold text-primary mb-4"
          >
            FEATURES &amp; BENEFITS
          </h2>
          <p className="text-neutral-500 text-lg mb-12 max-w-prose">
            Everything you need to discover, complete, and track your training
            — all in one place.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Link
                key={feature.title}
                to={feature.href}
                className="group relative flex flex-col items-center rounded-[1.75rem] border border-secondary/10 bg-white px-6 pb-5 pt-5 text-center shadow-[0_18px_45px_rgba(0,43,71,0.08)] transition-all duration-300 hover:-translate-y-2 hover:border-secondary/35 hover:shadow-[0_24px_55px_rgba(0,82,136,0.16)] focus-visible:-translate-y-2 focus-visible:border-secondary/35 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-secondary/20 active:scale-[0.99] active:translate-y-0 touch-manipulation"
              >
                <div className="relative z-10">
                  <feature.Icon className="h-14 w-14" />
                </div>
                <h3 className="mt-3 text-lg font-bold text-primary transition-colors group-hover:text-secondary group-focus-visible:text-secondary">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm font-semibold text-success">
                  {feature.highlight}
                </p>
                <p className="mt-2 text-[0.875rem] leading-relaxed text-neutral-600">
                  {feature.description}
                </p>
                <span className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-secondary group-hover:underline group-focus-visible:underline">
                  Learn More
                  <ArrowRight size={14} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Users / Roles */}
      <section
        className="py-16 sm:py-20 bg-neutral-50"
        aria-labelledby="users-heading"
      >
        <div className="mx-auto max-w-7xl px-4">
          <h2
            id="users-heading"
            className="text-3xl sm:text-4xl font-bold text-primary mb-4"
          >
            WHO USES CETA
          </h2>
          <p className="text-neutral-500 text-lg mb-12 max-w-prose">
            Three types of users interact with the platform, each with a
            tailored experience.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {userTypes.map((user) => (
              <div
                key={user.title}
                className="group relative overflow-hidden rounded-2xl border border-primary/15 min-h-[23rem] shadow-sm"
              >
                <img
                  src={user.image}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  style={{ objectPosition: user.imagePosition }}
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,43,71,0.24)_0%,rgba(0,43,71,0.7)_40%,rgba(0,43,71,0.94)_100%)]" />
                <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(180deg,rgba(0,82,136,0.42)_0%,rgba(0,82,136,0)_100%)]" />

                <div className="relative flex h-full flex-col p-8">
                  <div className="mb-6 flex items-center gap-4">
                    <div className="h-16 w-16 overflow-hidden rounded-full border-2 border-white/70 shadow-lg ring-4 ring-white/12">
                      <img
                        src={user.image}
                        alt=""
                        aria-hidden="true"
                        className="h-full w-full object-cover"
                        style={{ objectPosition: user.imagePosition }}
                      />
                    </div>
                    <div className="rounded-full bg-white/12 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur-sm">
                      {user.accentLabel}
                    </div>
                  </div>

                  <div className="mt-auto rounded-2xl border border-white/12 bg-primary/45 p-5 backdrop-blur-[2px]">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {user.title}
                    </h3>
                    <p className="text-secondary-lighter font-semibold text-sm mb-3">
                      {user.subtitle}
                    </p>
                    <p className="text-white/90 text-[0.9375rem] leading-relaxed mb-4">
                      {user.description}
                    </p>
                    <Link
                      to={user.href}
                      className="inline-flex items-center gap-1 text-white font-semibold text-sm hover:underline"
                    >
                      {user.linkText} <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 sm:py-20 bg-primary"
        aria-labelledby="cta-heading"
      >
        <div className="mx-auto max-w-7xl px-4 text-center">
          <h2
            id="cta-heading"
            className="text-2xl sm:text-3xl font-bold text-white mb-4"
          >
            Ready to get started?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-prose mx-auto">
            Discover the right training, complete it at your own pace, and
            leave with a visible status and transcript — no help needed.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-success hover:bg-success-hover text-white font-bold px-8 py-4 rounded text-base uppercase tracking-wider transition-colors"
          >
            Create Your Account
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  )
}
